import dot from 'dot-object';
import {
  MenuItemConfig,
  MenuLeafItemConfig,
  isDropdownMenuItem,
} from '@common/menus/menu-config';
import {User} from '@ui/types/user';
import {Settings} from '@ui/settings/settings';

interface FilterContext {
  user?: User | null;
  hasPermission: (permission: string) => boolean;
  settings: Settings;
}

export function filterMenuItems(
  items: MenuItemConfig[],
  ctx: FilterContext,
): MenuItemConfig[] {
  const filtered: MenuItemConfig[] = [];

  for (const item of items) {
    const processed = filterMenuItem(item, ctx);
    if (processed) {
      filtered.push(processed);
    }
  }

  return filtered;
}

function filterMenuItem(
  item: MenuItemConfig,
  ctx: FilterContext,
): MenuItemConfig | null {
  if (isDropdownMenuItem(item)) {
    const nestedItems = filterLeafMenuItems(item.items ?? [], ctx);
    if (!nestedItems.length) {
      return null;
    }
    return {...item, items: nestedItems};
  }

  return filterLeafMenuItem(item, ctx);
}

function filterLeafMenuItems(
  items: MenuLeafItemConfig[],
  ctx: FilterContext,
): MenuLeafItemConfig[] {
  const filtered: MenuLeafItemConfig[] = [];

  for (const item of items) {
    const processed = filterLeafMenuItem(item, ctx);
    if (processed) {
      filtered.push(processed);
    }
  }

  return filtered;
}

function filterLeafMenuItem(
  item: MenuLeafItemConfig,
  ctx: FilterContext,
): MenuLeafItemConfig | null {
  const hasRoles = (item.roles || []).every(roleId =>
    ctx.user?.roles.find(role => role.id === roleId),
  );
  const hasPermissions = (item.permissions || []).every(permission =>
    ctx.hasPermission(permission),
  );
  const hasSettings =
    !item.settings ||
    Object.entries(item.settings).every(([key, value]) => {
      return dot.pick(key, ctx.settings) == value;
    });

  if (
    !item.action ||
    !hasRoles ||
    !hasPermissions ||
    !hasSettings ||
    !subscriptionStatusMatches(item, ctx.user)
  ) {
    return null;
  }

  const action =
    ctx.user?.id && item.action
      ? item.action.replace(/{currentUser}/g, `${ctx.user.id}`)
      : item.action;

  return {...item, action};
}

function subscriptionStatusMatches(
  item: MenuLeafItemConfig,
  user?: User | null,
): boolean {
  if (!item.subscriptionStatus) {
    return true;
  }
  const hasActiveSubscription = !!user?.subscriptions?.find(sub => sub.active);
  if (item.subscriptionStatus === 'subscribed') {
    return hasActiveSubscription;
  }
  return !hasActiveSubscription;
}
